/* istanbul ignore file */
import { navContentStore } from "@phn/state/nav-content-store";
import { Fragment, getAssetPath, h } from "@stencil/core";
import { getMenuContent } from "@phn/services/menu-items-service";
import { getConfig, isDesktop } from "@phn/utility/helper";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import {
  ClickElementTypes,
  ElementTypes,
  EventActions,
} from "@phn/utility/constants";
import analyticsManager from "@phn/services/analytics/analytics-manager";
import { getCurrentRoute } from "@phn/services/routing-manager";
import featureToggles from "@phn/services/feature-toggles";
import { navStateStore } from "@phn/state/nav-state-store";
import {
  Series,
  TMenu,
  TVehiclePurchaseMenu,
} from "../../../../../shared/entities/content";

interface ElectricOwnershipTypes {
  activeRoutingKey: string;
  topLevelRoutingKey: string;
  breakpoint: number;
  receivedContent: boolean;
}

export default function ElectricOwnership({
  activeRoutingKey,
  topLevelRoutingKey,
  breakpoint,
  receivedContent,
}: ElectricOwnershipTypes): HTMLElement {
  const showHeadline = !isDesktop(breakpoint);
  const displayedContent = getMenuContent(
    navContentStore.state,
    activeRoutingKey,
    topLevelRoutingKey,
  ) as TVehiclePurchaseMenu;

  /* istanbul ignore if */
  if (displayedContent === null) {
    return <div></div>;
  }

  const allContent = displayedContent.children.findIndex((item) =>
    item.id.endsWith(".all"),
  );
  const tilesIndex = allContent !== -1 ? allContent : 0;
  const tiles = displayedContent.children[tilesIndex] as TVehiclePurchaseMenu;

  const menu = structuredClone(displayedContent);
  menu.children.splice(tilesIndex, 1);

  let firstItem: TMenu;
  if (featureToggles.isNewElectricOwnershipEnabled()) {
    // eslint-disable-next-line prefer-destructuring
    firstItem = menu.children[0];
    menu.children = menu.children.slice(1);
  }

  return (
    <Fragment>
      {tilesIndex === 0 ? (
        <TileComponent
          tiles={tiles}
          series={navContentStore.state.models.series}
          isMobile={showHeadline}
          firstItem={firstItem}
        />
      ) : null}

      <phn-nd-menu
        item={menu}
        showHeadline={showHeadline}
        receivedContent={receivedContent}
      />

      {tilesIndex !== 0 ? (
        <TileComponent
          tiles={tiles}
          series={navContentStore.state.models.series}
          isMobile={showHeadline}
          firstItem={firstItem}
        />
      ) : null}
    </Fragment>
  );
}

function TileComponent({
  tiles,
  isMobile,
  series,
  firstItem,
}: {
  tiles: TVehiclePurchaseMenu;
  isMobile: boolean;
  series: Series[];
  firstItem: TMenu;
}) {
  return (
    <div class="electric-ownership">
      {featureToggles.isNewElectricOwnershipEnabled() ? (
        <Fragment>
          <phn-p-link-pure
            stretch={!isMobile}
            align-label="start"
            href={firstItem.link}
            size="medium"
            class="overview-link"
            onClick={() => trackModelTileClick(tiles[0])}
          >
            {firstItem.text}
          </phn-p-link-pure>

          {tiles.children.map((tile, index) => (
            <phn-p-link-tile
              aria-label={tile.text}
              href={tile.link}
              onClick={() => trackModelTileClick(tile)}
              description={tile.text}
              key={tile.id}
              compact={true}
              size="medium"
              aspect-ratio={index === 0 ? "4/3" : "16/9"}
              weight="regular"
              tabindex="0"
            >
              <img
                srcset={index === 0 ? getImg("electric") : getImg("charge")}
                alt={tile.text}
              />
            </phn-p-link-tile>
          ))}
        </Fragment>
      ) : (
        <Fragment>
          <phn-p-text size="inherit" color="contrast-medium">
            {tiles.text}
          </phn-p-text>

          {tiles.children.map((tile) => {
            let image = series.find(
              (s) => s.image === tile.id.split(".").at(-1),
            )?.modelCompareImage;

            if (image) {
              const url = `${new URL(image).toString()}/m/0x${String(Math.round(window.devicePixelRatio * 510))}`;
              image = url.toString();
            }

            return (
              <phn-p-link-tile
                aria-label={tile.text}
                href={tile.link}
                onClick={() => trackModelTileClick(tile)}
                description={tile.text}
                key={tile.id}
                compact={true}
                size={isMobile ? "large" : "medium"}
                aspect-ratio="16/9"
                tabindex="0"
              >
                {image ? <img src={image} alt={tile.text} /> : null}
              </phn-p-link-tile>
            );
          })}
        </Fragment>
      )}
    </div>
  );
}

function trackModelTileClick(tile: TMenu): void {
  GlobalDataLayerService.getInstance().pushDataLayerEvent(
    EventActions.MODEL_CLICK,
    {
      componentClick: {
        clickElementId: tile.id,
        clickElementName: tile.text,
        clickElementType: ClickElementTypes.Link,
        targetUrl: tile.link,
      },
      componentDisplay: {
        displayElementId: analyticsManager.calculateDisplayElementId(),
        displayElementType: analyticsManager.calculateDisplayElementType(
          getCurrentRoute(),
          ElementTypes.bodyTypeButton,
        ),
        displayElementName: tile.text,
      },
    },
  );
}

function getImg(img: string): string {
  const assetsUrl = getConfig(navStateStore.state.env).ASSETS_URL;
  const assetPath = getAssetPath(`${assetsUrl}/series-assets`);
  return `${assetPath}/${img}.webp, ${assetPath}/${img}@2x.webp 2x`;
}
